import { useState, useCallback } from 'react';
import { useApi } from '../../../shared/hooks/useApi';
import { friendshipAPI, type FriendshipLink, type CreateFriendshipLinkCommand, type Friendship } from '../../../services/friendshipApi';

export function useFriendshipLinks() {
  const [links, setLinks] = useState<FriendshipLink[]>([]);
  const [activeLinks, setActiveLinks] = useState<FriendshipLink[]>([]);
  
  const {
    execute: fetchLinks,
    isLoading: isLoadingLinks,
    error: linksError
  } = useApi<FriendshipLink[]>(friendshipAPI.getUserLinks);
  
  const {
    execute: fetchActiveLinks,
    isLoading: isLoadingActiveLinks,
    error: activeLinksError
  } = useApi<FriendshipLink[]>(friendshipAPI.getUserActiveLinks);
  
  const {
    execute: createLink,
    isLoading: isCreating,
    error: createError
  } = useApi<FriendshipLink>(friendshipAPI.createFriendshipLink);
  
  const {
    execute: executeLinkUse,
    isLoading: isUsing,
    error: useError
  } = useApi<Friendship>(friendshipAPI.useFriendshipLink);
  
  const {
    execute: deactivateLink,
    isLoading: isDeactivating,
    error: deactivateError
  } = useApi<void>(friendshipAPI.deactivateLink);
  
  const {
    execute: deleteLink,
    isLoading: isDeleting,
    error: deleteError
  } = useApi<void>(friendshipAPI.deleteLink);
  
  const loadLinks = useCallback(async () => {
    const result = await fetchLinks();
    if (result) {
      setLinks(result);
    }
    return result;
  }, [fetchLinks]);
  
  const loadActiveLinks = useCallback(async () => {
    const result = await fetchActiveLinks();
    if (result) {
      setActiveLinks(result);
    }
    return result;
  }, [fetchActiveLinks]);
  
  const createFriendshipLink = useCallback(async (command: CreateFriendshipLinkCommand) => {
    const result = await createLink(command);
    if (result) {
      // Refresh links to show the new one
      await Promise.all([loadLinks(), loadActiveLinks()]);
    }
    return result;
  }, [createLink, loadLinks, loadActiveLinks]);
  
  const useFriendshipLink = useCallback(async (token: string) => {
    const result = await executeLinkUse(token);
    return result;
  }, [executeLinkUse]);
  
  const deactivateFriendshipLink = useCallback(async (linkId: string) => {
    const result = await deactivateLink(linkId);
    if (result !== null) { // Success (void result)
      // Remove from active links and update in all links
      setActiveLinks(prev => prev.filter(link => link.id !== linkId));
      setLinks(prev => prev.map(link => 
        link.id === linkId 
          ? { ...link, isActive: false }
          : link
      ));
    }
    return result;
  }, [deactivateLink]);
  
  const deleteFriendshipLink = useCallback(async (linkId: string) => {
    const result = await deleteLink(linkId);
    if (result !== null) { // Success (void result)
      // Remove from both lists
      setLinks(prev => prev.filter(link => link.id !== linkId));
      setActiveLinks(prev => prev.filter(link => link.id !== linkId));
    }
    return result;
  }, [deleteLink]);
  
  const loadAllLinks = useCallback(async () => {
    await Promise.all([
      loadLinks(),
      loadActiveLinks()
    ]);
  }, [loadLinks, loadActiveLinks]);
  
  return {
    links,
    activeLinks,
    isLoading: isLoadingLinks || isLoadingActiveLinks || isCreating || isUsing || isDeactivating || isDeleting,
    error: linksError || activeLinksError || createError || useError || deactivateError || deleteError,
    loadLinks,
    loadActiveLinks,
    loadAllLinks,
    createFriendshipLink,
    useFriendshipLink,
    deactivateFriendshipLink,
    deleteFriendshipLink
  };
}